function isPlainObject(value) {
  return value != null && typeof value === "object" && !Array.isArray(value);
}

export function cloneBlockTree(node) {
  if (node == null) return node;
  return JSON.parse(JSON.stringify(node));
}

function appearanceBackgroundToLayers(background) {
  if (!background) return null;
  if (Array.isArray(background)) return background;
  if (!isPlainObject(background) || !background.type) return null;
  if (background.type === "solid") {
    const color = Array.isArray(background.colors) ? background.colors[0] : null;
    return color ? [{ type: "solid", color }] : null;
  }
  if (background.type === "linear_gradient" || background.type === "radial_gradient") {
    return [{
      type: background.type,
      colors: background.colors,
      angle: background.angle,
      center_x: background.center_x,
      center_y: background.center_y,
    }];
  }
  if (background.type === "image") {
    return [{
      type: "image",
      image_url: background.image_url,
      scale: background.scale,
    }];
  }
  return null;
}

function mergePlain(base, patch) {
  if (!isPlainObject(patch)) return base;
  return { ...(isPlainObject(base) ? base : {}), ...patch };
}

function variantNamesFor(node, inherited) {
  const names = new Set(inherited);
  if (node.selectedState) names.add("selected");
  return names;
}

function applyVariantNames(node, names) {
  let current = node;
  for (const name of names) {
    const patch = isPlainObject(node.variants) ? node.variants[name] : null;
    if (!isPlainObject(patch)) continue;
    current = {
      ...current,
      appearance: mergePlain(current.appearance, patch.appearance),
      layout: mergePlain(current.layout, patch.layout),
    };
  }
  if (names.has("selected")) current = { ...current, selectedState: true };
  return current;
}

/** Overlay `variants` from inherited selection (e.g. active nav row). Does not replace `items`. */
export function applyActiveVariants(node, _groupValues, inherited = []) {
  if (!isPlainObject(node)) return node;
  const names = variantNamesFor(node, inherited);
  const patched = applyVariantNames(node, names);
  if (Array.isArray(node.items)) {
    patched.items = node.items.map((child) => applyActiveVariants(child, undefined, names));
  }
  return patched;
}

/** @deprecated Host selection no longer uses interaction.group; kept for call-site compatibility. */
export function defaultGroupValues(node, found = {}) {
  if (!isPlainObject(node)) return found;
  for (const child of node.items || []) defaultGroupValues(child, found);
  return found;
}

/** Flatten appearance, layout, and interaction onto the node the paint helpers read. */
export function normalizeBlockForPaint(node) {
  if (!node || typeof node !== "object") return node;
  const appearance = node.appearance && typeof node.appearance === "object" ? node.appearance : {};
  const interaction = node.interaction && typeof node.interaction === "object" ? node.interaction : {};
  const layout = node.layout && typeof node.layout === "object" ? { ...node.layout } : {};
  const direction = layout.direction;
  if (direction) delete layout.direction;

  const background = appearance.background
    ? appearanceBackgroundToLayers(appearance.background)
    : node.background;

  const interactionAction = interaction.action;
  const interactionShow = interaction.show;
  let action;
  if (typeof interactionAction === "string" && interactionAction) {
    action = { event: interactionAction, show: typeof interactionShow === "string" ? interactionShow : undefined };
  } else if (typeof interactionShow === "string" && interactionShow) {
    action = { show: interactionShow };
  }

  const normalized = {
    ...node,
    text: appearance.text ?? node.text,
    color: appearance.color ?? node.color,
    visibility: appearance.visibility ?? node.visibility,
    font: appearance.font ?? node.font,
    background,
    border: appearance.border ?? node.border,
    accessibility: appearance.accessibility ?? node.accessibility,
    source: appearance.source ?? node.source,
    action,
    dataset: interaction.dataset ?? node.dataset,
    direction: direction ?? node.direction,
    layout: Object.keys(layout).length ? layout : node.layout,
  };

  delete normalized.appearance;
  delete normalized.interaction;

  if (Array.isArray(normalized.items)) {
    normalized.items = normalized.items
      .map((child) => normalizeBlockForPaint(child))
      .filter(Boolean);
  }
  return normalized;
}

export function findNodeById(node, id) {
  if (!node || typeof node !== "object" || !id) return null;
  if (node.id === id) return node;
  for (const child of node.items || []) {
    const found = findNodeById(child, id);
    if (found) return found;
  }
  return null;
}

/** Replace a phase container's items and merge the phase layout patch onto that row. */
export function fillPhaseContainer(row, phase) {
  if (!row || !phase) return;
  if (phase.layout && typeof phase.layout === "object") {
    const layout = cloneBlockTree(phase.layout);
    if (layout.direction) {
      row.direction = layout.direction;
      delete layout.direction;
    }
    row.layout = { ...(row.layout ?? {}), ...layout };
  }
  const items = Array.isArray(phase.items) ? phase.items : [];
  row.items = items.map((item) => cloneBlockTree(item)).filter(Boolean);
}
